import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import shallow from 'zustand/shallow';

import { getVoucherList } from '../queries';
import { useAuthStore, useCartStore, useShipToStore } from '../stores';
import { useCartTotal } from './useCartTotal';

export const VOUCHER_LIST_KEY = 'VoucherList';

export const useVoucherList = (enabled = true) => {
  const accessToken = useAuthStore((state) => state.access_token);
  const warehouse = useShipToStore((s) => s.selectedShipToId, shallow);

  const cartItems = useCartStore((state) => state.cartItems, shallow);
  const cartRecord = useCartStore((state) => state.cartRecord, shallow);

  const {
    data: cartTotal,
    isSuccess: isCartTotalReady,
    isFetching: isCartTotalFetching,
  } = useCartTotal();

  const hydrated =
    typeof window === 'undefined'
      ? false
      : useCartStore.persist?.hasHydrated?.() ?? false;

  const voucherCartItems = useMemo(
    () =>
      cartItems.map((id) => ({
        product_item_code: cartRecord[id]?.product?.item_code ?? 0,
        quantity: cartRecord[id]?.quantity ?? 0,
      })),
    [cartItems, cartRecord]
  );

  const voucherCartItemsKey = useMemo(
    () => JSON.stringify(voucherCartItems),
    [voucherCartItems]
  );

  const hasCartTotal = isCartTotalReady && !isCartTotalFetching;

  // Voucher min-total rules must be validated against the total AFTER product
  // promo discounts but BEFORE voucher discounts. `final_total` from cartTotal
  // is post-voucher when voucher_codes are applied, so we add back the voucher
  // discount to get the correct eligibility base.
  const eligibilityTotalAmount =
    (cartTotal?.final_total ?? 0) +
    (cartTotal?.vouchers?.total_voucher_discount ?? 0);

  return useInfiniteQuery({
    queryKey: [
      VOUCHER_LIST_KEY,
      warehouse,
      eligibilityTotalAmount,
      voucherCartItemsKey,
    ],
    queryFn: ({ pageParam }) =>
      getVoucherList(accessToken, {
        warehouse_id: warehouse,
        cart_total_amount: eligibilityTotalAmount,
        items: voucherCartItems,
        page: pageParam,
        size: 20,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.pages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    enabled:
      enabled &&
      hydrated &&
      !!warehouse &&
      !!accessToken &&
      cartItems.length > 0 &&
      hasCartTotal,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
  });
};
